import { IconFile } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NSButton, NSDropdown } from 'bricks';
import useToast from 'hooks/useToast';
import useAwsUploadFiles from 'hooks/useAWSUpload';
import { useAuthContext } from 'contexts/AuthContext';
import { FEATURES_LIST } from 'services/constants';
import { QUERY_KEYS } from 'services/urls';
import { useGetDealDirectoryTree, useUpdateDocument } from 'views/DocumentLibrary/hooks';
import { IDealDocument } from 'views/DocumentLibrary/types';
import DropzoneCard from 'views/DocumentLibrary/components/DropzoneCard/DropzoneCard';
import { useFilePicker, PickedFile } from 'views/DocumentLibrary/hooks/useFilePicker';
import { useGetTimelineDocuments } from '../../../hooks/useGetTimelineDocuments';
import { useRemoveTimelineDocument } from '../../../hooks/useRemoveTimelineDocument';
import AttachmentRow from './AttachmentRow/AttachmentRow';
import PickFromDocumentsModal from './PickFromDocumentsModal/PickFromDocumentsModal';
import './TimelineSlideoutAttachments.scss';
import invariant from 'helpers/invariant';
import { useDocumentUploader, FileMeta, UploadedFileMeta } from 'views/DocumentLibrary/hooks/useDocumentUploader';
import { findDocumentInTree } from '../../../helpers';
import { SORT_OPTIONS } from 'views/DealDashboard/constants';
import { SortOption } from 'views/DealDashboard/types';

interface TimelineSlideoutAttachmentsProps {
    timelineId: number;
    dealId: number;
    onAttachmentCountChange: (count: number) => void;
}

const TimelineSlideoutAttachments: React.FC<TimelineSlideoutAttachmentsProps> = ({
    timelineId,
    dealId,
    onAttachmentCountChange,
}) => {
    const { selectedOrganizationId } = useAuthContext();
    invariant(selectedOrganizationId, 'Organization ID is required');

    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();

    const [sortOrder, setSortOrder] = useState<SortOption>('newest');
    const [showPickerModal, setShowPickerModal] = useState(false);
    const [renamingDocumentId, setRenamingDocumentId] = useState<number | null>(null);

    // Track if we've already triggered upload for current batch
    const uploadTriggeredRef = useRef(false);

    const {
        data: timelineDocuments,
        isLoading: isLoadingDocuments,
    } = useGetTimelineDocuments({
        orgId: selectedOrganizationId,
        dealId,
        timelineId,
    });

    const { data: directoryTree } = useGetDealDirectoryTree({
        dealId,
        options: { enabled: !!dealId },
    });

    const { mutate: updateDocument } = useUpdateDocument();
    const { mutate: removeTimelineDocument } = useRemoveTimelineDocument();

    const {
        selectedHTML5FileToUpload,
        setSelectedHTML5FileToUpload,
        awsUploadedFiles,
        setAwsUploadedFiles,
        uploadFilesWithLimit,
        clearAttachmentsStates,
        isReadyToUpload,
        setIsReadyToUpload,
    } = useAwsUploadFiles({
        organizationCorrelationId: selectedOrganizationId,
        feature: FEATURES_LIST.DEAL_DOCUMENTS,
        onError: (message: string) => {
            showError(`Failed to upload file: ${message}`);
        },
    });

    const uploadFiles = useDocumentUploader({
        dealId,
        organizationId: selectedOrganizationId,
        selectedDirectoryId: 0, // Home directory
        showSuccess,
        showError,
        clearAttachmentsStates,
        setAwsUploadedFiles,
        activityIds: [timelineId],
    });

    useEffect(() => {
        onAttachmentCountChange(timelineDocuments?.length ?? 0);
    }, [timelineDocuments?.length, onAttachmentCountChange]);

    // Check if all files are uploaded and ready to process
    const shouldUploadFiles = useMemo(() => {
        return (
            awsUploadedFiles.length > 0
            && awsUploadedFiles.length === selectedHTML5FileToUpload.length
            && isReadyToUpload
        );
    }, [awsUploadedFiles.length, selectedHTML5FileToUpload.length, isReadyToUpload]);

    // Handle file upload completion
    useEffect(() => {
        if (shouldUploadFiles && !uploadTriggeredRef.current) {
            uploadTriggeredRef.current = true;
            uploadFiles(
                selectedHTML5FileToUpload as FileMeta[],
                awsUploadedFiles as UploadedFileMeta[],
            );
        }
        
        if (awsUploadedFiles.length === 0 && uploadTriggeredRef.current) {
            uploadTriggeredRef.current = false;
        }
    }, [shouldUploadFiles, awsUploadedFiles, selectedHTML5FileToUpload, uploadFiles]);

    const getFilesFromEvent = useFilePicker({
        onAddFiles: (newFiles: PickedFile[]) => {
            const files = newFiles.map(f => ({ id: f.id, file: f.file }));
            setSelectedHTML5FileToUpload(prev => [...prev, ...files]);
        },
        onCardDrop: async (files: PickedFile[]) => {
            const toUpload = files.map(f => ({ id: f.id, file: f.file }));
            setIsReadyToUpload(true);
            await uploadFilesWithLimit(toUpload);
        },
        onNewFolders: () => {},
        onNewTopLevelFiles: () => {},
        currentDirCardView: 0, // Home directory
    });

    const sortedDocuments = useMemo(() => {
        if (!timelineDocuments) return [];
        return [...timelineDocuments].sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
    }, [timelineDocuments, sortOrder]);

    const attachedDocumentIds = useMemo(() => new Set(
        (timelineDocuments ?? []).map(doc => doc.id),
    ), [timelineDocuments]);

    const handleRename = useCallback(async (documentId: number, newName: string) => {
        setRenamingDocumentId(documentId);
        const doc = timelineDocuments?.find(d => d.id === documentId);
        
        updateDocument(
            { documentId, data: { name: newName, directoryId: doc?.directoryId ?? null } },
            {
                onSuccess: () => {
                    showSuccess('Document renamed successfully');
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TIMELINE_DOCUMENTS, selectedOrganizationId, dealId, timelineId] });
                },
                onError: () => {
                    showError('Failed to rename document');
                },
                onSettled: () => {
                    setRenamingDocumentId(null);
                },
            },
        );

    }, [updateDocument, timelineId, timelineDocuments, selectedOrganizationId, dealId, queryClient, showSuccess, showError]);

    const handleRemove = useCallback((documentId: number) => {
        removeTimelineDocument(
            { orgId: selectedOrganizationId, dealId, timelineId, documentId },
            {
                onSuccess: () => {
                    showSuccess('Attachment removed from task');
                    queryClient.invalidateQueries({ 
                        queryKey: [QUERY_KEYS.TIMELINE_DOCUMENTS, selectedOrganizationId, dealId, timelineId],
                        refetchType: 'active',
                    });
                },
                onError: () => {
                    showError('Failed to remove attachment');
                },
            },
        );
    }, [removeTimelineDocument, timelineId, dealId, selectedOrganizationId, queryClient, showSuccess, showError]);

    const handleAttachFromPicker = useCallback((selectedDocumentIds: number[]) => {
        selectedDocumentIds.forEach(documentId => {
            const doc = (directoryTree?.documents ?? []).find((d: IDealDocument) => d.id === documentId)
                || findDocumentInTree(directoryTree ?? null, documentId);

            const existingActivities = doc?.timelines?.map(t => t.dealTimelineId) ?? [];
            const newActivities = [...new Set([...existingActivities, timelineId])];

            updateDocument(
                { 
                    documentId, 
                    data: { 
                        activities: newActivities, 
                        directoryId: doc?.directoryId ?? null,
                    },
                },
                {
                    onSuccess: () => {
                        showSuccess(`Document "${doc?.name}" attached successfully`);
                        queryClient.invalidateQueries({ 
                            queryKey: [QUERY_KEYS.TIMELINE_DOCUMENTS, selectedOrganizationId, dealId, timelineId],
                            refetchType: 'active',
                        });
                    },
                    onError: () => {
                        showError(`Failed to attach document "${doc?.name}"`);
                    },
                },
            );
        });

    }, [directoryTree, selectedOrganizationId, dealId, timelineId, updateDocument, queryClient, showSuccess, showError]);

    return (
        <div className="TimelineSlideoutAttachments p-3">
            <div className="TimelineSlideoutAttachments__dropzone  mb-3">
                <DropzoneCard
                    getFilesFromEvent={getFilesFromEvent}
                    allowFolderUpload={false}
                />
                <div className="d-flex align-items-center mb-3">
                    <hr className="flex-grow-1" />
                    <span className="text-muted font-12 mx-2">OR</span>
                    <hr className="flex-grow-1" />
                </div>
                <div className="text-center mb-3">
                    <NSButton
                        color="secondary"
                        callback={() => setShowPickerModal(true)}
                    >
                        <NSIcon icon={IconFile} className="mr-2" />
                        Pick from Documents &rsaquo;
                    </NSButton>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted font-12">
                    {`ATTACHED (${timelineDocuments?.length ?? 0})`}
                </span>
                {(timelineDocuments?.length ?? 0) > 0 && (
                    <NSDropdown
                        options={SORT_OPTIONS}
                        selectedOption={SORT_OPTIONS.find(o => o.value === sortOrder)}
                        handleSelectOption={(option) => setSortOrder(option.value as SortOption)}
                        toggleClassName="bg-transparent border-0"
                    />
                )}
            </div>
            <div className="TimelineSlideoutAttachments__list">
                {isLoadingDocuments ? (
                    <div className="text-muted text-center py-4">Loading attachments...</div>
                ) : sortedDocuments.length === 0 ? (
                    <div className="text-muted text-center py-4">No attachments yet.</div>
                ) : (
                    sortedDocuments.map(doc => (
                        <AttachmentRow
                            key={doc.id}
                            document={doc}
                            onRename={handleRename}
                            onRemove={handleRemove}
                            isRenameSaving={renamingDocumentId === doc.id}
                        />
                    ))
                )}
            </div>
            <PickFromDocumentsModal
                isOpen={showPickerModal}
                toggle={() => setShowPickerModal(false)}
                directoryTree={directoryTree ?? null}
                attachedDocumentIds={attachedDocumentIds}
                onConfirm={handleAttachFromPicker}
            />
        </div>
    );
};

export default TimelineSlideoutAttachments;
